import { useEffect, useRef, useState } from 'react';

// Global dialog state
let globalDialogCount = 0;
const dialogStateListeners = new Set<(isOpen: boolean) => void>();

// Helper functions to manage global dialog state
export const incrementDialogCount = () => {
  globalDialogCount++;
  notifyListeners();
};

export const decrementDialogCount = () => {
  globalDialogCount = Math.max(0, globalDialogCount - 1);
  notifyListeners();
};

const notifyListeners = () => {
  const isAnyDialogOpen = globalDialogCount > 0;
  dialogStateListeners.forEach((listener) => listener(isAnyDialogOpen));
};

// Custom hook to track if any dialog is open
export const useDialogState = () => {
  const [isAnyDialogOpen, setIsAnyDialogOpen] = useState(globalDialogCount > 0);

  useEffect(() => {
    const listener = (isOpen: boolean) => {
      setIsAnyDialogOpen(isOpen);
    };

    dialogStateListeners.add(listener);

    // Set initial state
    setIsAnyDialogOpen(globalDialogCount > 0);

    return () => {
      dialogStateListeners.delete(listener);
    };
  }, []);

  return isAnyDialogOpen;
};

// Hook for dialog components to register themselves
export const useDialogRegistration = (isOpen: boolean) => {
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // State changed from closed to open
    if (isOpen && !wasOpenRef.current) {
      incrementDialogCount();
      wasOpenRef.current = true;
    }
    // State changed from open to closed
    else if (!isOpen && wasOpenRef.current) {
      decrementDialogCount();
      wasOpenRef.current = false;
    }

    // Cleanup when component unmounts while dialog is open
    return () => {
      if (wasOpenRef.current) {
        decrementDialogCount();
        wasOpenRef.current = false;
      }
    };
  }, [isOpen]);
};
